// ThinkingPanel component for displaying step-by-step reasoning process
// Wabi-sabi style: minimal, borderless, light text, clean design

import React from 'react'
import { ThinkingStep } from '../lib/types'
import { CheckCircle2, Loader2, Circle } from 'lucide-react'

interface ThinkingPanelProps {
  steps: ThinkingStep[]
  isStreaming?: boolean
}

export function ThinkingPanel({ steps, isStreaming = false }: ThinkingPanelProps) {
  if (!steps || steps.length === 0) {
    return null
  }

  const getStatusIcon = (status: ThinkingStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3 text-gray-400 dark:text-gray-500" />
      case 'running':
        return <Loader2 className="h-3 w-3 text-gray-400 dark:text-gray-500 animate-spin" />
      case 'pending':
        return <Circle className="h-3 w-3 text-gray-300 dark:text-gray-600" />
    }
  }

  return (
    <div className="mt-2 mb-2">
      <div className="space-y-2">
        {steps.map((step) => (
          <div
            key={step.id}
            className="flex items-start gap-2.5"
          >
            {/* Minimal status indicator */}
            <div className="mt-0.5 flex-shrink-0 opacity-60">
              {getStatusIcon(step.status)}
            </div>
            
            {/* Step content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs font-light text-gray-400 dark:text-gray-500">
                  {step.title}
                </span>
                {step.tool_name && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-extralight italic opacity-70">
                    {step.tool_name}
                  </span>
                )}
              </div>
              {step.content && (
                <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 leading-relaxed whitespace-pre-wrap font-extralight">
                  {step.content}
                  {step.status === 'running' && (
                    <span className="inline-block w-0.5 h-3 ml-1 bg-gray-400 dark:bg-gray-500 animate-pulse align-middle opacity-50" />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
